import React, { forwardRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { INDENTATION_WIDTH } from "../utilities/menuTree";

/**
 * Presentational row. Used both for the live sortable rows and the floating
 * DragOverlay clone.
 */
export const TreeItem = forwardRef(function TreeItem(
  {
    item,
    depth,
    clone,
    ghost,
    childCount,
    collapsed,
    onCollapse,
    onRemove,
    onEdit,
    handleProps,
    wrapperStyle,
    style,
    ...props
  },
  ref
) {
  return (
    <Box
      ref={ref}
      style={{ paddingLeft: clone ? 0 : depth * INDENTATION_WIDTH, ...wrapperStyle }}
      sx={{ listStyle: "none", boxSizing: "border-box", mb: 0.75 }}
      {...props}
    >
      <Box
        style={style}
        className="menu-tree-row"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.75,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          opacity: ghost ? 0.45 : 1,
          boxShadow: clone ? 6 : 0,
          transition: "border-color .15s, box-shadow .15s",
          "&:hover": { borderColor: "primary.main" },
        }}
      >
        <Box
          {...handleProps}
          className="menu-tree-handle"
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "text.disabled",
            touchAction: "none",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        {onCollapse && childCount > 0 ? (
          <IconButton size="small" onClick={onCollapse} sx={{ p: 0.25 }}>
            {collapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 22 }} />
        )}

        {item?.MenuNavItem_Icon ? (
          <i
            className={item.MenuNavItem_Icon}
            style={{ width: 18, textAlign: "center", opacity: 0.8 }}
          />
        ) : (
          <Box sx={{ width: 18 }} />
        )}

        <Typography
          variant="body2"
          sx={{ fontWeight: 500, flex: "0 1 auto", whiteSpace: "nowrap" }}
        >
          {item?.MenuNavItem_Name || "Untitled"}
        </Typography>

        {item?.MenuNavItem_Url && item.MenuNavItem_Url !== "#" && (
          <Chip
            label={`/${item.MenuNavItem_Url}`}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: 11, color: "text.secondary" }}
          />
        )}

        {clone && childCount > 1 && (
          <Chip
            label={childCount}
            size="small"
            color="primary"
            sx={{ height: 20, ml: 0.5 }}
          />
        )}

        {!clone && (
          <Box sx={{ ml: "auto", display: "flex", gap: 0.25 }}>
            {onEdit && (
              <Tooltip title="Edit item">
                <IconButton size="small" onClick={onEdit} sx={{ p: 0.5 }}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onRemove && (
              <Tooltip title="Remove from menu">
                <IconButton
                  size="small"
                  onClick={onRemove}
                  sx={{ p: 0.5, color: "error.light" }}
                >
                  <RemoveCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
});

/** Sortable wrapper around TreeItem. */
export default function SortableTreeItem({ id, depth, ...props }) {
  const {
    attributes,
    listeners,
    setDraggableNodeRef,
    setDroppableNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <TreeItem
      ref={setDroppableNodeRef}
      wrapperStyle={{}}
      style={style}
      depth={depth}
      ghost={isDragging}
      handleProps={{ ref: setDraggableNodeRef, ...attributes, ...listeners }}
      {...props}
    />
  );
}
